import React, { useState, useRef, useEffect } from "react"
import { useSelector } from "react-redux"

import { StoreType } from "client/store"
import { CrossIcon } from "client/components"
import {
    SearchBarWrapper,
    SearchBarInputWrapper,
    SearchBarSelectedFilterWrapper,
    SearchBarInput,
    SearchBarPopupWrapper,
    SearchBarPopupSidebar,
    SearchBarPopupForm,
    SelectedFilter,
    SelectedFilterTitle,
    PopupFilter,
    PopupFilterTitle,
    CloseButton,
    ClearSearch,
} from "./styled"


interface Filters {
    search?: string
}

type UpdateFilter = (filters: Filters) => Filters

export interface SearchFilter {
    title: string
    isActive?: boolean
    onAdd?: UpdateFilter
    onClose: UpdateFilter
}

export interface SearchBarProps {
    id: string
    filters: Filters
    leftOffset?: boolean
    rightOffset?: boolean
    popupFilters?: SearchFilter[]
    extendedFilters?: SearchFilter[]
    filterForm?: React.ReactNode
    onFilter: (filters: Filters) => void
}

export const SearchBar = ({
    id,
    filters,
    leftOffset = false,
    rightOffset = false,
    popupFilters = [],
    extendedFilters = [],
    filterForm = null,
    onFilter,
}: SearchBarProps) => {
    const [showPopup, setShowPopup] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const popupRef = useRef<HTMLDivElement>(null)
    const lang = useSelector((state: StoreType) => state.system.lang)

    useEffect(() => {
        // hide popup when new filters are transferred
        setShowPopup(false)
    }, [filters])

    useEffect(() => {
        const togglePopup = (e: MouseEvent) => {
            const target = e.target as HTMLElement
            if (target.tagName === "I") return

            // don't hide popup when editing extended form
            if (popupRef.current && popupRef.current.contains(e.target as Node)) return

            // show/hide on input click
            if (!showPopup && inputRef.current && inputRef.current.contains(e.target as Node)) {
                setShowPopup(true)
            } else {
                setShowPopup(false)
            }
        }

        document.addEventListener("click", togglePopup)
        return () => {
            document.removeEventListener("click", togglePopup)
        }
    }, [showPopup])


    // prepare selected filters
    const selectedPopupFilters: SearchFilter[] = []
    popupFilters.forEach((filter) => {
        if (filter.isActive) selectedPopupFilters.push(filter)
    })
    const selectedFilters = selectedPopupFilters.concat(extendedFilters)

    // limit number of displayed filters
    const displayFilters: SearchFilter[] = []
    const combinedTitle: string[] = []
    const combinedClose: UpdateFilter[] = []
    let combinedNumber: number = 0
    const maxDisplay = 2
    selectedFilters.forEach((filter) => {
        if (
            displayFilters.length < maxDisplay
            || selectedFilters.length <= maxDisplay + 1
        ) {
            displayFilters.push(filter)
        } else {
            combinedTitle.push(filter.title)
            combinedClose.push(filter.onClose)
            combinedNumber += 1
        }
    })

    const onCombinedClose = () => {
        let newFilters = { ...filters }
        combinedClose.forEach((action) => {
            newFilters = action(newFilters)
        })
        onFilter(newFilters)
    }

    const onChangeSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newFilters: Filters = {
            ...filters,
            search: e.target.value,
        }
        onFilter(newFilters)
    }

    const onClearSearch = () => {
        onFilter({
            search: null,
        })
    }

    return (
        <SearchBarWrapper
            leftOffset={leftOffset}
            rightOffset={rightOffset}
        >
            <SearchBarInputWrapper>
                <SearchBarSelectedFilterWrapper>
                    {
                        displayFilters.map((filter) => (
                            <SelectedFilter key={filter.title}>
                                <SelectedFilterTitle title={filter.title}>
                                    {filter.title}
                                </SelectedFilterTitle>
                                <CloseButton onClick={() => {
                                    const newFilters = filter.onClose(filters)
                                    onFilter(newFilters)
                                }}
                                >
                                    <CrossIcon />
                                </CloseButton>
                            </SelectedFilter>
                        ))
                    }
                    {
                        combinedNumber > 0 && (
                            <SelectedFilter>
                                <SelectedFilterTitle title={combinedTitle.join(",\n")}>
                                    {`and more ${combinedNumber}`}
                                </SelectedFilterTitle>
                                <CloseButton onClick={onCombinedClose}>
                                    <CrossIcon />
                                </CloseButton>
                            </SelectedFilter>
                        )
                    }
                </SearchBarSelectedFilterWrapper>
                <SearchBarInput
                    ref={inputRef}
                    id={id}
                    placeholder={
                        popupFilters.length === 0
                            ? lang.searchPlaceholder
                            : (
                                displayFilters.length
                                    ? lang.searchWithActiveFilterPlaceholder
                                    : lang.searchWithFilterPlaceholder
                            )
                    }
                    value={filters && filters.search ? filters.search : ""}
                    onChange={onChangeSearchString}
                    autoComplete="off"
                />
                <ClearSearch onClick={onClearSearch}>
                    <CrossIcon />
                </ClearSearch>
            </SearchBarInputWrapper>
            <SearchBarPopupWrapper
                ref={popupRef}
                show={showPopup && (popupFilters.length > 0 || filterForm !== null)}
                showForm={filterForm !== null}
            >
                <SearchBarPopupSidebar show={popupFilters.length > 0}>
                    {
                        popupFilters.map((filter) => (
                            <PopupFilter
                                key={filter.title}
                                onClick={() => {
                                    const newFilters = filter.onAdd(filters)
                                    onFilter(newFilters)
                                }}
                            >
                                <PopupFilterTitle isActive={filter.isActive}>
                                    {filter.title}
                                </PopupFilterTitle>
                            </PopupFilter>
                        ))
                    }
                </SearchBarPopupSidebar>
                <SearchBarPopupForm show={filterForm !== null}>
                    { filterForm && filterForm }
                </SearchBarPopupForm>
            </SearchBarPopupWrapper>
        </SearchBarWrapper>
    )
}
